import { OnerwayPaymentMethod } from 'pixai-contract/payment/shared'
import type { FC, ReactElement, ReactNode } from 'react'
import { tv } from 'tailwind-variants'
import LogosAmex from '~icons/logos/amex'
import LogosDiscover from '~icons/logos/discover'
import LogosJcb from '~icons/logos/jcb'
import LogosMastercard from '~icons/logos/mastercard'
import LogosUnionpay from '~icons/logos/unionpay'
import LogosVisa from '~icons/logos/visa'
import MaterialSymbolsLocalConvenienceStoreOutline from '~icons/material-symbols/local-convenience-store-outline'
import SimpleIconsWechat from '~icons/simple-icons/wechat'
import GooglePayLogo from 'src/components/Payment/google-pay-logo.svg'
import { PayPayLogo } from 'src/components/Payment/PayPayLogo'
import { Currency } from 'src/services/apollo/generated/graphql'
import { PaymentProvider } from 'src/services/helpers/payment'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'

type PaymentProviderAvailabilityPredicate = ({
  country,
}: {
  country?: string
  type: 'membership' | 'credit-pack' | 'unknown'
  prices?: Record<Currency, number>
  /**
   * Active subscription state — predicates can gate on "upgrade vs new
   * subscription" (e.g. one-time methods only work when backend dispatches
   * subProductType=DIRECT, not SUBSCRIBE).
   */
  hasActiveSubscription?: boolean
}) => boolean

export type PaymentProviderConfig = {
  id: PaymentProvider
  name: ReactNode

  paymentMethod?: OnerwayPaymentMethod

  supportedCurrencies: Currency[]
  icon: ReactNode

  available: boolean | PaymentProviderAvailabilityPredicate
  preferred?: PaymentProviderAvailabilityPredicate
  new?: boolean
}

const CardBrandIconTile: FC<{ children: ReactElement }> = ({ children }) => (
  <span className="flex h-5 w-[30px] shrink-0 items-center justify-center rounded-sm bg-white px-0.5 shadow-sm ring-1 ring-black/10 sm:h-6 sm:w-9 [&>svg]:max-h-[13px] [&>svg]:max-w-full sm:[&>svg]:max-h-[15px]">
    {children}
  </span>
)

const CardBrandIconGroup: FC<{ brands: ReactElement[] }> = ({ brands }) => (
  <div className="flex min-w-0 max-w-full items-center gap-1 overflow-hidden sm:gap-1.5">
    {brands.map((brand, index) => (
      <CardBrandIconTile key={brand.key ?? index}>{brand}</CardBrandIconTile>
    ))}
  </div>
)

export const PAYMENT_PROVIDERS: PaymentProviderConfig[] = [
  {
    id: PaymentProvider.onerway,
    name: 'Visa/Master/Discover',
    paymentMethod: OnerwayPaymentMethod.CARD,
    supportedCurrencies: [Currency.Usd],
    icon: (
      <CardBrandIconGroup
        brands={[
          <LogosVisa key="visa" />,
          <LogosMastercard key="mastercard" />,
          <LogosDiscover key="discover" />,
        ]}
      />
    ),
    available: true,
    preferred: () => true,
  },
  {
    id: PaymentProvider.onerwayJcbAeUnionPay,
    name: 'JCB/Amex/UnionPay',
    paymentMethod: OnerwayPaymentMethod.CARD,
    supportedCurrencies: [Currency.Usd],
    icon: (
      <CardBrandIconGroup
        brands={[
          <LogosJcb key="jcb" />,
          <LogosAmex key="amex" />,
          <LogosUnionpay key="unionpay" />,
        ]}
      />
    ),
    available: ({ type }) => type === 'credit-pack' || type === 'membership',
  },
  {
    id: PaymentProvider.paypay,
    name: 'PayPay',
    supportedCurrencies: [Currency.Jpy],
    icon: <PayPayLogo className="w-5 h-5" />,
    available: ({ country }) => country === 'JP',
    preferred: () => true,
  },
  {
    id: PaymentProvider.gmo,
    name: 'コンビニ決済',
    supportedCurrencies: [Currency.Jpy],
    icon: <MaterialSymbolsLocalConvenienceStoreOutline className="w-5 h-5" />,
    available: ({ country, prices }) =>
      country === 'JP' && (prices?.[Currency.Jpy] ?? 0) >= 1000,
    preferred: () => true,
  },
  {
    id: PaymentProvider.onerwayGooglePay,
    name: 'Google Pay',
    paymentMethod: OnerwayPaymentMethod.GOOGLE_PAY,
    supportedCurrencies: [Currency.Usd],
    icon: (
      <img
        src={GooglePayLogo}
        alt="google pay logo"
        className="h-8 w-8 object-contain text-white sm:h-7 sm:w-auto"
      />
    ),
    available: true,
  },
  {
    id: PaymentProvider.onerwayWechatPay,
    name: 'WeChat Pay',
    // No paymentMethod here — desktop sends wechat_qr; mobile shows a
    // desktop-only notice (see OnerwayWechatPay.tsx).
    supportedCurrencies: [Currency.Usd],
    icon: <SimpleIconsWechat className="w-5 h-5 text-[#07C160]" />,
    // One-time-only (LPMS can't auto-renew). Backend routes through
    // handleOneTimeMembership when oneTimeMembershipId is provided.
    available: true,
    new: true,
  },
]

const styles = tv({
  slots: {
    container:
      'relative overflow-hidden border-2 border-solid p-3 flex h-14 w-full min-w-0 items-center gap-2 rounded-lg bg-neutral-800 text-left text-white tracking-tight select-none transition duration-100 hover:brightness-110 active:brightness-95 sm:h-[84px] sm:flex-col sm:items-start sm:justify-center sm:gap-2',
    icon: 'flex h-6 min-w-0 shrink-0 items-center justify-start max-sm:max-w-[42%] sm:h-7 sm:w-full',
    name: 'flex min-w-0 flex-1 items-center text-sm font-medium leading-tight text-white sm:w-full sm:flex-none',
    nameText:
      'block min-w-0 max-w-full truncate sm:whitespace-normal sm:break-words',
    badge:
      'pointer-events-none absolute right-2 top-2 z-10 max-w-[calc(100%-16px)] whitespace-nowrap rounded-sm bg-purple-500 px-1 py-0.5 text-[9px] font-medium leading-none text-white shadow-[0_1px_5px_rgb(0_0_0/0.25)] ring-1 ring-white/20',
    rightIconContainer: 'h-8 w-8 flex items-center justify-center',
    rightIcon: 'w-6 h-6 opacity-80',
  },
  variants: {
    selected: {
      true: {
        container: 'border-neutral-100',
      },
      false: {
        container: 'border-neutral-600',
      },
    },
  },
})

export const PaymentMethodSelectItem: FC<{
  id: PaymentProvider | null
  badge?: ReactElement
  selected?: boolean
  onClick?: () => void
}> = ({ id, badge, selected, onClick }) => {
  const isMobile = useBreakpoint('sm')
  const config = PAYMENT_PROVIDERS.find(p => p.id === id)

  const {
    container,
    icon,
    name,
    nameText,
    badge: badgeStyle,
  } = styles({ selected })

  if (!config) {
    return null
  }

  if (isMobile) {
    return (
      <button className={container()} onClick={onClick}>
        <div className={icon()}>{config.icon}</div>
        <div className={name()}>
          <span className={nameText()}>{config.name}</span>
        </div>
        {badge && <div className={badgeStyle()}>{badge}</div>}
      </button>
    )
  }

  return (
    <button className={container()} onClick={onClick}>
      <div className={icon()}>{config.icon}</div>
      <div className={name()}>
        <span className={nameText()}>{config.name}</span>
      </div>
      {badge && <div className={badgeStyle()}>{badge}</div>}
    </button>
  )
}
